import { useState, useEffect, useRef, useCallback } from 'react'
import Card from '../ui/Card'
import { teamMembersAPI } from '../../lib/services'
import type { TeamMember } from '../../lib/services'
import { ChevronRight, ChevronUp, Plus, Trash2, Star } from 'lucide-react'

interface ContactsCardProps {
  vendorId: number
  teamMembers: TeamMember[]
  onRefresh: () => void
}

const CLEARANCE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'none', label: 'No Clearance' },
  { value: 'confidential', label: 'Confidential' },
  { value: 'secret', label: 'Secret' },
  { value: 'top_secret', label: 'Top Secret' },
  { value: 'top_secret_sci', label: 'Top Secret/SCI' },
]

const CITIZENSHIP_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'us_citizen', label: 'U.S. Citizen' },
  { value: 'permanent_resident', label: 'Permanent Resident' },
  { value: 'foreign_national', label: 'Foreign National' },
]

function useDebouncedSave(memberId: number, onRefresh: () => void, delay = 400) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const pendingRef = useRef<Partial<TeamMember>>({})

  const save = useCallback((field: string, value: any) => {
    pendingRef.current = { ...pendingRef.current, [field]: value }
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(async () => {
      const patch = { ...pendingRef.current }
      pendingRef.current = {}
      try {
        await teamMembersAPI.update(memberId, patch)
        onRefresh()
      } catch (err) {
        console.error('Failed to save team member:', err)
      }
    }, delay)
  }, [memberId, delay, onRefresh])

  const flush = useCallback(async () => {
    if (Object.keys(pendingRef.current).length > 0) {
      clearTimeout(timeoutRef.current)
      const patch = { ...pendingRef.current }
      pendingRef.current = {}
      try {
        await teamMembersAPI.update(memberId, patch)
        onRefresh()
      } catch (err) {
        console.error('Failed to save team member:', err)
      }
    }
  }, [memberId, onRefresh])

  useEffect(() => () => { flush() }, [flush])

  return { save, flush }
}

function ContactForm({ member, onRefresh, onDelete, onClose }: {
  member: TeamMember
  onRefresh: () => void
  onDelete: () => void
  onClose: () => void
}) {
  const [local, setLocal] = useState(member)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const { save, flush } = useDebouncedSave(member.id, onRefresh)

  useEffect(() => { setLocal(member) }, [member])

  const handleText = (field: keyof TeamMember, value: string) => {
    setLocal(prev => ({ ...prev, [field]: value }))
    save(field, value)
  }

  const handleImmediate = async (field: keyof TeamMember, value: any) => {
    await flush()
    setLocal(prev => ({ ...prev, [field]: value }))
    try {
      await teamMembersAPI.update(member.id, { [field]: value } as any)
      onRefresh()
    } catch (err) {
      console.error('Failed to save:', err)
    }
  }

  const handleDelete = async () => {
    try {
      await teamMembersAPI.delete(member.id)
      onDelete()
      onRefresh()
    } catch (err) {
      console.error('Failed to delete:', err)
    }
  }

  return (
    <div className="space-y-3 pt-3 pb-1">
      <Field label="Name">
        <input
          type="text"
          value={local.name}
          onChange={e => handleText('name', e.target.value)}
          className="h-7 w-full text-sm border border-border rounded-md bg-background text-foreground px-2 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Full name"
        />
      </Field>
      <Field label="Title">
        <input
          type="text"
          value={local.title || ''}
          onChange={e => handleText('title', e.target.value)}
          className="h-7 w-full text-sm border border-border rounded-md bg-background text-foreground px-2 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Job title"
        />
      </Field>
      <Field label="Role">
        <input
          type="text"
          value={local.role || ''}
          onChange={e => handleText('role', e.target.value)}
          className="h-7 w-full text-sm border border-border rounded-md bg-background text-foreground px-2 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="e.g. Technical POC, Contracts"
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          value={local.email || ''}
          onChange={e => handleText('email', e.target.value)}
          className="h-7 w-full text-sm border border-border rounded-md bg-background text-foreground px-2 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="email@company.com"
        />
      </Field>
      <Field label="Phone">
        <input
          type="tel"
          value={local.phone || ''}
          onChange={e => handleText('phone', e.target.value)}
          className="h-7 w-full text-sm border border-border rounded-md bg-background text-foreground px-2 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="(555) 123-4567"
        />
      </Field>
      <Field label="LinkedIn">
        <input
          type="url"
          value={local.linkedin_url || ''}
          onChange={e => handleText('linkedin_url', e.target.value)}
          className="h-7 w-full text-sm border border-border rounded-md bg-background text-foreground px-2 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="https://linkedin.com/in/..."
        />
      </Field>

      <div className="border-t border-border pt-3 mt-3 space-y-3">
        <Field label="Primary Contact">
          <BooleanToggle
            value={local.is_primary_contact}
            onChange={v => handleImmediate('is_primary_contact', v)}
          />
        </Field>
        <Field label="Founder">
          <BooleanToggle
            value={local.is_founder}
            onChange={v => handleImmediate('is_founder', v)}
          />
        </Field>
        <Field label="C-Suite">
          <BooleanToggle
            value={local.is_c_suite}
            onChange={v => handleImmediate('is_c_suite', v)}
          />
        </Field>
      </div>

      <div className="border-t border-border pt-3 mt-3 space-y-3">
        <Field label="Clearance">
          <SegmentedSelect
            options={CLEARANCE_OPTIONS}
            value={local.security_clearance || 'unknown'}
            onChange={v => handleImmediate('security_clearance', v)}
          />
        </Field>
        <Field label="Citizenship">
          <SegmentedSelect
            options={CITIZENSHIP_OPTIONS}
            value={local.citizenship_status || 'unknown'}
            onChange={v => handleImmediate('citizenship_status', v)}
          />
        </Field>
      </div>

      <div className="border-t border-border pt-3 mt-3 flex items-center justify-between">
        <button
          onClick={onClose}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronUp className="w-3.5 h-3.5" />
          Close
        </button>
        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Delete?</span>
            <button
              onClick={handleDelete}
              className="px-2.5 py-1 text-xs rounded-md bg-red-500/15 border border-red-500/30 text-red-600 font-medium hover:bg-red-500/25 transition-colors"
            >
              Confirm
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-2.5 py-1 text-xs rounded-md border border-border text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-24 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

function SegmentedSelect({ options, value, onChange }: {
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map(o => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-2 py-1 text-xs rounded-md border transition-colors ${
            value === o.value
              ? 'bg-primary/15 border-primary/30 text-primary font-medium'
              : 'bg-transparent border-border text-muted-foreground hover:bg-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function BooleanToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(!value ? true : false)}
        className={`px-2.5 py-1 text-xs rounded-l-md border transition-colors ${
          value
            ? 'bg-green-500/15 border-green-500/30 text-green-600 font-medium'
            : 'bg-transparent border-border text-muted-foreground hover:bg-muted'
        }`}
      >
        Yes
      </button>
      <button
        onClick={() => onChange(value ? false : true)}
        className={`px-2.5 py-1 text-xs rounded-r-md border transition-colors ${
          !value
            ? 'bg-red-500/15 border-red-500/30 text-red-600 font-medium'
            : 'bg-transparent border-border text-muted-foreground hover:bg-muted'
        }`}
      >
        No
      </button>
    </div>
  )
}

export default function ContactsCard({ vendorId, teamMembers, onRefresh }: ContactsCardProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)

  const handleToggle = (id: number) => {
    setExpandedId(prev => prev === id ? null : id)
  }

  const handleAddContact = async () => {
    setAdding(true)
    try {
      const newMember = await teamMembersAPI.create({ vendor: vendorId, name: '' } as any)
      onRefresh()
      setExpandedId(newMember.id)
    } catch (err) {
      console.error('Failed to create contact:', err)
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = (id: number) => {
    if (expandedId === id) setExpandedId(null)
  }

  if (teamMembers.length === 0 && !adding) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">Contacts</h3>
        </div>
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground mb-3">No contacts yet</p>
          <button
            onClick={handleAddContact}
            disabled={adding}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border text-foreground hover:bg-muted transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-foreground">Contacts</h3>
        <button
          onClick={handleAddContact}
          disabled={adding}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Add contact"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-1">
        {teamMembers.map(member => (
          <div key={member.id}>
            <button
              onClick={() => handleToggle(member.id)}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted/50 transition-colors text-left"
            >
              <ChevronRight
                className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${
                  expandedId === member.id ? 'rotate-90' : ''
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-foreground truncate">
                    {member.name || 'Unnamed'}
                  </span>
                  {member.is_primary_contact && (
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />
                  )}
                </div>
                {(member.title || member.role) && (
                  <span className="text-xs text-muted-foreground truncate block">
                    {member.title || member.role}
                  </span>
                )}
              </div>
            </button>

            {expandedId === member.id && (
              <div className="ml-6 mr-1 border-l-2 border-border pl-3">
                <ContactForm
                  member={member}
                  onRefresh={onRefresh}
                  onDelete={() => handleDelete(member.id)}
                  onClose={() => setExpandedId(null)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}
